import { isMattedBorder, type FramedProduct } from '@rpl/pricing';
import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { centeredAutoCropRect } from '../../../lib/printCheck';
import { rotatedDims, type Rotation } from '../../../lib/rotate';
import { bwCssFilter } from '../../../lib/bw';
import type { BwStyle, CropRect } from '../../../lib/types';

/** "8x10" → [8, 10]; falls back to a square if unparseable. */
function parseInches(s: string): [number, number] {
  const [a, b] = s.split('x').map(Number);
  return [a || 1, b || 1];
}

/** Contain-fit an aspect (w/h) within an outer box. */
function containBox(outerW: number, outerH: number, aspect: number): { w: number; h: number } {
  let w = outerW;
  let h = aspect > 0 ? outerW / aspect : 0;
  if (h > outerH) {
    h = outerH;
    w = outerH * aspect;
  }
  return { w, h };
}

interface LivePhoto {
  previewUrl: string | null;
  width: number;
  height: number;
}

/**
 * LIVE print-area renderer (round 7): draws the CURRENT crop region of the
 * (rotated, optionally B&W) image so it exactly fills the frame's photo
 * opening — the crop editor's adjustments render inside the frame as you make
 * them. The box is measured (ResizeObserver) because rotated geometry needs px
 * math: the rotated image is scaled so the crop rect fills the box, then the
 * unrotated <img> is centred on that footprint and CSS-rotated. `fit` mode
 * contains the whole image on white instead.
 */
function LiveFramedImg({
  photo,
  printInches,
  cropRect,
  rotation,
  bw,
  bwStyle = null,
  bwStrength = null,
  fit,
}: {
  photo: LivePhoto;
  printInches: [number, number];
  cropRect: CropRect | null;
  rotation: Rotation;
  bw: boolean;
  bwStyle?: BwStyle | null;
  bwStrength?: number | null;
  fit: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [rw, rh] = rotatedDims(photo.width, photo.height, rotation);
  let img: CSSProperties | null = null;
  if (photo.previewUrl && box.w > 0 && box.h > 0 && rw > 0 && rh > 0) {
    const swap = rotation === 90 || rotation === 270;
    if (fit) {
      // Whole image contained in the opening; white shows around it.
      const c = containBox(box.w, box.h, rw / rh);
      const iw = swap ? c.h : c.w;
      const ih = swap ? c.w : c.h;
      img = {
        left: (box.w - iw) / 2,
        top: (box.h - ih) / 2,
        width: iw,
        height: ih,
      };
    } else {
      const r = cropRect ?? centeredAutoCropRect(rw, rh, printInches);
      if (r.w > 0 && r.h > 0) {
        // Rotated-image display size so the crop rect fills the box exactly.
        const dw = box.w / r.w;
        const dh = box.h / r.h;
        const cx = -r.x * dw + dw / 2; // rotated-footprint centre in box px
        const cy = -r.y * dh + dh / 2;
        const iw = swap ? dh : dw; // the unrotated <img> swaps dims at 90/270
        const ih = swap ? dw : dh;
        img = { left: cx - iw / 2, top: cy - ih / 2, width: iw, height: ih };
      }
    }
  }

  return (
    <div ref={ref} className={`fpreview__live${fit ? ' fpreview__live--fit' : ''}`}>
      {img && photo.previewUrl && (
        <img
          src={photo.previewUrl}
          alt=""
          draggable={false}
          style={{
            ...img,
            ...(rotation !== 0 ? { transform: `rotate(${rotation}deg)` } : {}),
            ...(bw ? { filter: bwCssFilter(bw, bwStyle, bwStrength) } : {}),
          }}
        />
      )}
    </div>
  );
}

/**
 * CSS-only sample of the framed result: a black timber frame around the chosen
 * photo, either matted (Double Matt Border — warm matt reveal around a smaller
 * print) or print-to-edge (the print fills the frame). No images beyond the
 * customer's own preview — the frame + matt are pure CSS.
 *
 * LIVE MODE (round 7, crop editor): pass `cropRect` / `rotation` / `bw` /
 * `fit` and the opening renders the ACTUAL print area — crop, rotation and
 * black & white update live while editing. Without them the photo simply
 * covers the opening (centred crop), as before.
 */
export function FramePreview({
  photo,
  option,
  cropRect,
  rotation = 0,
  bw = false,
  bwStyle = null,
  bwStrength = null,
  fit = false,
}: {
  photo: LivePhoto | undefined;
  option: FramedProduct;
  /** Live crop rect (rotated-image space); null = centered auto-crop. */
  cropRect?: CropRect | null;
  /** Live rotation — also flips the frame's orientation with the image. */
  rotation?: Rotation;
  /** Render the print in black & white. */
  bw?: boolean;
  bwStyle?: BwStyle | null;
  bwStrength?: number | null;
  /** Fit mode: contain the whole image on white instead of cropping. */
  fit?: boolean;
}) {
  const matted = isMattedBorder(option.border);
  const live = cropRect !== undefined || rotation !== 0 || bw || fit;

  const { frameStyle, photoW, photoH } = useMemo(() => {
    // Orientation follows the (rotated) image so a 90° turn re-orients the frame.
    const [rw, rh] =
      photo && photo.width > 0 ? rotatedDims(photo.width, photo.height, rotation) : [0, 0];
    const landscape = rw > 0 ? rw >= rh : option.printInches[0] >= option.printInches[1];
    const orient = ([a, b]: [number, number]): [number, number] => {
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      return landscape ? [hi, lo] : [lo, hi];
    };
    // frameInches, not parseInches(frameSize): "A2" parses to [NaN] and the
    // [a || 1, b || 1] fallback below turned that into a white 1:1 square.
    const [fw, fh] = orient(option.frameInches);
    const [pw, ph] = orient(option.printInches);
    // Fix the constraining dimension so aspect-ratio derives the other without
    // clashing with a max-width/height cap (which would distort the frame).
    // Round 9: the caps read the --fpv-w / --fpv-h budget vars so a host (the
    // Configure stage) can scale the mock with the screen; the defaults keep
    // every other surface exactly as before. Landscape also caps its width
    // from the height budget (h = w / ar → w ≤ maxH · ar) so it never
    // outgrows a host's height budget.
    const frameStyle = landscape
      ? {
          aspectRatio: `${fw} / ${fh}`,
          width: `min(var(--fpv-w, 260px), calc(var(--fpv-h, 230px) * ${fw / fh}), 100%)`,
        }
      : { aspectRatio: `${fw} / ${fh}`, height: 'var(--fpv-h, 230px)' };
    return {
      frameStyle,
      photoW: `${Math.min(100, (pw / fw) * 100)}%`,
      photoH: `${Math.min(100, (ph / fh) * 100)}%`,
    };
  }, [photo, option, rotation]);

  return (
    <div
      // The colour modifier carries the moulding gradient, the outer shadow
      // that separates a white frame from a white host, and the matt seam.
      // It lives on the FRAME, so every host inherits it for free.
      className={
        `fpreview${matted ? ' fpreview--matted' : ' fpreview--edge'}` +
        (option.colour && option.colour !== 'black' ? ` fpreview--${option.colour}` : '')
      }
      aria-hidden="true"
    >
      <div className="fpreview__frame" style={frameStyle}>
        <div className="fpreview__mount">
          <div className="fpreview__photo" style={{ width: photoW, height: photoH }}>
            {live && photo ? (
              <LiveFramedImg
                photo={photo}
                printInches={option.printInches}
                cropRect={cropRect ?? null}
                rotation={rotation}
                bw={bw}
                bwStyle={bwStyle}
                bwStrength={bwStrength}
                fit={fit}
              />
            ) : photo?.previewUrl ? (
              <img src={photo.previewUrl} alt="" />
            ) : (
              <span className="fpreview__ph" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
